
import React, { useState, useEffect } from 'react';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  Paper,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import axios from 'axios';

const HearingAidTraining = () => {
  const [opNumber, setOpNumber] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [patientData, setPatientData] = useState(null);
  const [demographyData, setDemographyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState({ message: '', severity: '' });
  const [openPasswordModal, setOpenPasswordModal] = useState(false);
  const [password, setPassword] = useState('');
  const [downloadFormat, setDownloadFormat] = useState('excel');

  const [hearingInfo, setHearingInfo] = useState({
    date: '',
    pd: '',
    configuration: '',
    durationHL: '',
    onset: '',
    firstTimeUser: '',
    userType: '',
    dailyUsage: '',
    advice: ''
  });

  const [testingConditions, setTestingConditions] = useState({
    condition: '',
    scheme: '',
    ear: '',
    presentationLevel: '',
    mode: '',
    testingEnvironment: '',
    snrValue: '',
    response500: '',
    response1khz: '',
    response2khz: '',
    response4khz: '',
    responseWordLow: '',
    responseWordHigh: '',
    responseSentences: '',
    tolerance: '',
    recommendedHACompany: '',
    recommendedHAModel: '',
    recommendedHAStyle: ''
  });

  useEffect(() => {
    const raw = localStorage.getItem('demography');
    if (!raw) {
      setSaveStatus({ message: 'No registered patient found in localStorage. Please register a patient.', severity: 'info' });
      return;
    }

    try {
      const parsed = JSON.parse(raw);
      setDemographyData(parsed);

      const patient = Array.isArray(parsed) ? parsed[0] : parsed;

      const op = patient.opNumber || patient.op_number || patient.ipNumber || patient.ip_number || patient.value || '';

      if (!op) {
        setSaveStatus({ message: 'Demography found but no OP/IP number available.', severity: 'warning' });
        return;
      }

      setOpNumber(op);
      setPatientData({
        name: patient.name || patient.patientName || '',
        age: patient.age || patient.patient_age || '',
        ip_number: patient.ipNumber || patient.ip_number || '',
        op_number: op,
        phone: patient.phone || patient.mobile || '',
        address: patient.address || ''
      });
      setShowForm(true);

      (async () => {
        setLoading(true);
        try {
          try {
            const resp = await fetch(`${process.env.REACT_APP_API_URL}/api/hearing-aid-training/search/${op}`);
            if (resp.ok) {
              const remotePatient = await resp.json();
              if (remotePatient) {
                setPatientData(prev => ({ ...prev, ...remotePatient }));
              }
            }
          } catch (err) {
            console.warn('Could not fetch remote patient demographic', err);
          }

          try {
            const existingResp = await fetch(`${process.env.REACT_APP_API_URL}/api/hearing-aid-training/${op}`);
            if (existingResp.ok) {
              const existingData = await existingResp.json();
              if (existingData) {
                setHearingInfo(prev => ({ ...prev, ...(existingData.date ? existingData : {}) }));
                setTestingConditions(prev => ({ ...prev, ...(existingData.condition ? existingData : {}) }));
                setSaveStatus({ message: 'Loaded saved hearing/testing data (if any).', severity: 'success' });
              }
            }
          } catch (err) {
            console.warn('Could not fetch existing hearing/testing data', err);
          }
        } finally {
          setLoading(false);
        }
      })();
    } catch (error) {
      console.error('Error parsing demography from localStorage:', error);
      setSaveStatus({ message: 'Invalid demography data in localStorage', severity: 'error' });
    }
  }, []);

  const handleHearingInfoChange = (e) => {
    const { name, value } = e.target;
    setHearingInfo(prev => ({ ...prev, [name]: value }));
  };

  const handleTestingConditionsChange = (e) => {
    const { name, value } = e.target;
    setTestingConditions(prev => ({ ...prev, [name]: value }));
  };

  const saveHearingInfo = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/hearing-aid-training/save-hearing-info`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          opNumber,
          ...hearingInfo,
          patientName: patientData?.name || demographyData?.name,
          patientIpNumber: patientData?.ip_number || demographyData?.ipNumber || '',
          patientOpNumber: patientData?.op_number || demographyData?.opNumber || '',
          patientInternalNumber: demographyData?.internalNumber || '',
        }),
      });

      if (response.ok) {
        setSaveStatus({ message: 'Hearing information saved successfully!', severity: 'success' });
      } else {
        setSaveStatus({ message: 'Failed to save hearing information', severity: 'error' });
      }
    } catch (error) {
      console.error('Error saving hearing info:', error);
      setSaveStatus({ message: 'Error saving hearing information', severity: 'error' });
    }
  };

  const saveTestingConditions = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/hearing-aid-training/save-testing-conditions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          opNumber,
          ...testingConditions,
          patientName: patientData?.name || demographyData?.name,
          patientIpNumber: patientData?.ip_number || demographyData?.ipNumber || '',
          patientOpNumber: patientData?.op_number || demographyData?.opNumber || '',
          patientInternalNumber: demographyData?.internalNumber || '',
        }),
      });

      if (response.ok) {
        setSaveStatus({ message: 'Testing conditions saved successfully!', severity: 'success' });
      } else {
        setSaveStatus({ message: 'Failed to save testing conditions', severity: 'error' });
      }
    } catch (error) {
      console.error('Error saving testing conditions:', error);
      setSaveStatus({ message: 'Error saving testing conditions', severity: 'error' });
    }
  };

  const handleSubmit = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/hearing-aid-training/submit-all`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          opNumber,
          ...hearingInfo,
          ...testingConditions,
          patientName: patientData?.name || demographyData?.name,
          patientIpNumber: patientData?.ip_number || demographyData?.ipNumber || '',
          patientOpNumber: patientData?.op_number || demographyData?.opNumber || '',
          patientInternalNumber: demographyData?.internalNumber || '',
        }),
      });

      if (response.ok) {
        alert('Hearing aid trial data submitted successfully!');
        setHearingInfo({
          date: '',
          pd: '',
          configuration: '',
          durationHL: '',
          onset: '',
          firstTimeUser: '',
          userType: '',
          dailyUsage: '',
          advice: ''
        });
        setTestingConditions({
          condition: '',
          scheme: '',
          ear: '',
          presentationLevel: '',
          mode: '',
          testingEnvironment: '',
          snrValue: '',
          response500: '',
          response1khz: '',
          response2khz: '',
          response4khz: '',
          responseWordLow: '',
          responseWordHigh: '',
          responseSentences: '',
          tolerance: '',
          recommendedHACompany: '',
          recommendedHAModel: '',
          recommendedHAStyle: ''
        });
      } else {
        alert('Failed to submit hearing aid trial data');
      }
    } catch (error) {
      console.error('Error submitting hearing aid trial:', error);
      alert('Error submitting hearing aid trial data');
    }
  };

  const handleDownloadClick = () => {
    setOpenPasswordModal(true);
  };

const handleDownloadConfirm = async () => {
  const correctKey = "Varun@6361";

  // Validate key entered in modal
  if (password !== correctKey) {
    alert("Incorrect key! Download canceled.");
    setPassword('');
    setOpenPasswordModal(false);
    return;
  }

  setOpenPasswordModal(false);
  setLoading(true);
  setSaveStatus({
    message: `Requesting Hearing Aid Training ${downloadFormat}...`,
    severity: "info",
  });

  try {
    const resp = await fetch(
      `${process.env.REACT_APP_API_URL}/api/reports/download-form`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          table: "hearing_aid_training",
          formName: "Hearing_Aid_Training",
          format: downloadFormat,
          key: password   // 🔥 send KEY instead of password
        }),
      }
    );

    if (!resp.ok) {
      const body = await resp.json().catch(() => ({}));
      setSaveStatus({
        message: `Failed: ${body.message || resp.statusText}`,
        severity: "error",
      });
      return;
    }

    const blob = await resp.blob();
    const disposition = resp.headers.get("content-disposition") || "";

    let filename = `Hearing_Aid_Training_${Date.now()}.${
      downloadFormat === "excel" ? "xlsx" : "pdf"
    }`;

    const match = /filename="?([^"]+)"?/.exec(disposition);
    if (match && match[1]) filename = match[1];

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);

    setSaveStatus({
      message: `Download started: ${filename}`,
      severity: "success",
    });

  } catch (err) {
    console.error("Download error:", err);
    setSaveStatus({
      message: `Error downloading file: ${err.message}`,
      severity: "error",
    });
  } finally {
    setLoading(false);
    setPassword('');
  }
};


  const handlePasswordModalClose = () => {
    setOpenPasswordModal(false);
    setPassword('');
  };

  return (
    <Paper elevation={3} sx={{ p: 4, maxWidth: 1400, margin: 'auto', mt: 4, mb: 6, position: 'relative' }}>
      {/* Download Button - Top Right */}
      <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadClick}
          disabled={loading}
          sx={{ 
            borderRadius: 2,
            boxShadow: 2,
            '&:hover': {
              boxShadow: 4,
              transform: 'translateY(-2px)',
              transition: 'all 0.3s ease'
            }
          }}
        >
          Download 
        </Button>
      </Box>

      <Typography variant="h4" gutterBottom fontWeight="bold" color="secondary" align="center">
        Hearing Aid Trial
      </Typography>

      {saveStatus.message && (
        <Alert severity={saveStatus.severity} sx={{ mb: 2, mt: 1 }}>
          {saveStatus.message}
        </Alert>
      )}

      {loading && (
        <Box display="flex" justifyContent="center" sx={{ mb: 2 }}>
          <CircularProgress />
        </Box>
      )}


      {patientData && (
        <Paper elevation={2} sx={{ p: 3, mb: 3, backgroundColor: '#e3f2fd', borderRadius: 2 }}>
          <Typography variant="h5" gutterBottom fontWeight="bold" color="secondary">
            Patient Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography><strong>Name:</strong> {patientData.name}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography><strong>Age:</strong> {patientData.age}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography><strong>IP Number:</strong> {patientData.ip_number}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography><strong>OP Number:</strong> {patientData.op_number}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography><strong>Phone:</strong> {patientData.phone}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography><strong>Address:</strong> {patientData.address}</Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      {showForm && patientData && (
        <>
          {/* Hearing Information */}
          <Box mb={4} border={1} borderColor="divider" p={3} borderRadius={2}>
            <Typography variant="h5" gutterBottom fontWeight="bold" color="secondary">
              Hearing Information
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="date"
                  label="Date of Assessment"
                  type="date"
                  variant="outlined"
                  value={hearingInfo.date}
                  onChange={handleHearingInfoChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel shrink={true}>PD</InputLabel>
                  <Select
                    name="pd"
                    value={hearingInfo.pd}
                    onChange={handleHearingInfoChange}
                    label="PD"
                    displayEmpty
                  >
                    <MenuItem value=""><em>Select PD</em></MenuItem>
                    <MenuItem value="Left">Left</MenuItem>
                    <MenuItem value="Right">Right</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel shrink={true}>Configuration</InputLabel>
                  <Select
                    name="configuration"
                    value={hearingInfo.configuration}
                    onChange={handleHearingInfoChange}
                    label="Configuration"
                    displayEmpty
                  >
                    <MenuItem value=""><em>Select Configuration</em></MenuItem>
                    <MenuItem value="Flat">Flat</MenuItem>
                    <MenuItem value="Rising">Rising</MenuItem>
                    <MenuItem value="Sloping">Sloping</MenuItem>
                    <MenuItem value="Corner">Corner</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel shrink={true}>Duration of HL</InputLabel>
                  <Select
                    name="durationHL"
                    value={hearingInfo.durationHL}
                    onChange={handleHearingInfoChange}
                    label="Duration of HL"
                    displayEmpty
                  >
                    <MenuItem value=""><em>Select Duration</em></MenuItem>
                    <MenuItem value="less than 6 weeks">Less than 6 weeks</MenuItem>
                    <MenuItem value="6weeks to 11 months">6 weeks to 11 months</MenuItem>
                    <MenuItem value="1 to 10 years">1 to 10 years</MenuItem>
                    <MenuItem value="over 10 years">Over 10 years</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel shrink={true}>Onset</InputLabel>
                  <Select
                    name="onset"
                    value={hearingInfo.onset}
                    onChange={handleHearingInfoChange}
                    label="Onset"
                    displayEmpty
                  >
                    <MenuItem value=""><em>Select Onset</em></MenuItem>
                    <MenuItem value="Sudden">Sudden</MenuItem>
                    <MenuItem value="Gradual">Gradual</MenuItem>
                    <MenuItem value="Progressive">Progressive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel shrink={true}>First Time User</InputLabel>
                  <Select
                    name="firstTimeUser"
                    value={hearingInfo.firstTimeUser}
                    onChange={handleHearingInfoChange}
                    label="First Time User"
                    displayEmpty
                  >
                    <MenuItem value=""><em>Select Option</em></MenuItem>
                    <MenuItem value="yes">Yes</MenuItem>
                    <MenuItem value="no">No</MenuItem>
                    <MenuItem value="Naïve">Naïve</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel shrink={true}>User Type</InputLabel>
                  <Select
                    name="userType"
                    value={hearingInfo.userType}
                    onChange={handleHearingInfoChange}
                    label="User Type"
                    displayEmpty
                  >
                    <MenuItem value=""><em>Select User Type</em></MenuItem>
                    <MenuItem value="less than 6 weeks">Less than 6 weeks</MenuItem>
                    <MenuItem value="6weeks to 11 months">6 weeks to 11 months</MenuItem>
                    <MenuItem value="1 to 10 years">1 to 10 years</MenuItem>
                    <MenuItem value="over 10 years">Over 10 years</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel shrink={true}>Daily HA Usage Duration</InputLabel>
                  <Select
                    name="dailyUsage"
                    value={hearingInfo.dailyUsage}
                    onChange={handleHearingInfoChange}
                    label="Daily HA Usage Duration"
                    displayEmpty
                  >
                    <MenuItem value=""><em>Select Usage Duration</em></MenuItem>
                    <MenuItem value="Less than 1 hour/day">Less than 1 hour/day</MenuItem>
                    <MenuItem value="1 to 4 hours/day">1 to 4 hours/day</MenuItem>
                    <MenuItem value="4 to 8 hours/day">4 to 8 hours/day</MenuItem>
                    <MenuItem value="8 to 16 hours/day">8 to 16 hours/day</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel shrink={true}>Advice</InputLabel>
                  <Select
                    name="advice"
                    value={hearingInfo.advice}
                    onChange={handleHearingInfoChange}
                    label="Advice"
                    displayEmpty
                  >
                    <MenuItem value=""><em>Select Advice</em></MenuItem>
                    <MenuItem value="Reprogramming">Reprogramming</MenuItem>
                    <MenuItem value="Hearing Aid Repair">Hearing Aid Repair</MenuItem>
                    <MenuItem value="RECM">RECM</MenuItem>
                    <MenuItem value="Follow Up">Follow Up</MenuItem>
                    <MenuItem value="New Hearing Aid">New Hearing Aid</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={saveHearingInfo}
              >
                Save Hearing Info
              </Button>
            </Box>
          </Box>

          {/* Testing Conditions */}
          <Box mb={4} border={1} borderColor="divider" p={3} borderRadius={2}>
            <Typography variant="h5" gutterBottom fontWeight="bold" color="secondary">
              Testing Conditions
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel shrink={true}>Condition</InputLabel>
                  <Select
                    name="condition"
                    value={testingConditions.condition}
                    onChange={handleTestingConditionsChange}
                    label="Condition"
                    displayEmpty
                  >
                    <MenuItem value=""><em>Select Condition</em></MenuItem>
                    <MenuItem value="Unaided">Unaided</MenuItem>
                    <MenuItem value="Aided">Aided</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {testingConditions.condition === 'Aided' && (
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel shrink={true}>MOP</InputLabel>
                    <Select
                      name="scheme"
                      value={testingConditions.scheme}
                      onChange={handleTestingConditionsChange}
                      label="MOP"
                      displayEmpty
                    >
                      <MenuItem value=""><em>Select MOP</em></MenuItem>
                      <MenuItem value="HADU">HAD</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel shrink={true}>Ear</InputLabel>
                  <Select
                    name="ear"
                    value={testingConditions.ear}
                    onChange={handleTestingConditionsChange}
                    label="Ear"
                    displayEmpty
                  >
                    <MenuItem value=""><em>Select Ear</em></MenuItem>
                    <MenuItem value="Right">Right</MenuItem>
                    <MenuItem value="Left">Left</MenuItem>
                    <MenuItem value="Both">Both</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel shrink={true}>Presentation Level</InputLabel>
                  <Select
                    name="presentationLevel"
                    value={testingConditions.presentationLevel}
                    onChange={handleTestingConditionsChange}
                    label="Presentation Level"
                    displayEmpty
                  >
                    <MenuItem value=""><em>Select Level</em></MenuItem>
                    <MenuItem value="5 feet">5 feet</MenuItem>
                    <MenuItem value="30db">30db</MenuItem>
                    <MenuItem value="40db">40db</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel shrink={true}>Mode</InputLabel>
                  <Select
                    name="mode"
                    value={testingConditions.mode}
                    onChange={handleTestingConditionsChange}
                    label="Mode"
                    displayEmpty
                  >
                    <MenuItem value=""><em>Select Mode</em></MenuItem>
                    <MenuItem value="A">A</MenuItem>
                    <MenuItem value="A/V">A/V</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel shrink={true}>Testing Environment</InputLabel>
                  <Select
                    name="testingEnvironment"
                    value={testingConditions.testingEnvironment}
                    onChange={handleTestingConditionsChange}
                    label="Testing Environment"
                    displayEmpty
                  >
                    <MenuItem value=""><em>Select Environment</em></MenuItem>
                    <MenuItem value="Quiet">Quiet</MenuItem>
                    <MenuItem value="SNR">SNR</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {testingConditions.testingEnvironment === 'SNR' && (
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="SNR Value"
                    name="snrValue"
                    value={testingConditions.snrValue}
                    onChange={handleTestingConditionsChange}
                    variant="outlined"
                  />
                </Grid>
              )}

              {testingConditions.condition === 'Aided' && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Recommended Hearing Aid
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Company"
                      name="recommendedHACompany"
                      value={testingConditions.recommendedHACompany}
                      onChange={handleTestingConditionsChange}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Model"
                      name="recommendedHAModel"
                      value={testingConditions.recommendedHAModel}
                      onChange={handleTestingConditionsChange}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Style"
                      name="recommendedHAStyle"
                      value={testingConditions.recommendedHAStyle}
                      onChange={handleTestingConditionsChange}
                      variant="outlined"
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="500 Hz"
                  name="response500"
                  value={testingConditions.response500}
                  onChange={handleTestingConditionsChange}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="1 kHz"
                  name="response1khz"
                  value={testingConditions.response1khz}
                  onChange={handleTestingConditionsChange}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="2 kHz"
                  name="response2khz"
                  value={testingConditions.response2khz}
                  onChange={handleTestingConditionsChange}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="4 kHz"
                  name="response4khz"
                  value={testingConditions.response4khz}
                  onChange={handleTestingConditionsChange}
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Word (Low)"
                  name="responseWordLow"
                  value={testingConditions.responseWordLow}
                  onChange={handleTestingConditionsChange}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Word (High)"
                  name="responseWordHigh"
                  value={testingConditions.responseWordHigh}
                  onChange={handleTestingConditionsChange}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Sentences"
                  name="responseSentences"
                  value={testingConditions.responseSentences}
                  onChange={handleTestingConditionsChange}
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Tolerance"
                  name="tolerance"
                  value={testingConditions.tolerance}
                  onChange={handleTestingConditionsChange}
                  variant="outlined"
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={saveTestingConditions}
              >
                Save Testing Conditions
              </Button>
            </Box>
          </Box>

          <Box display="flex" justifyContent="center" mt={4}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              sx={{ px: 6, py: 1.5, fontSize: '1.1rem', borderRadius: 2 }}
              onClick={handleSubmit}
            >
              Submit Hearing Aid Trial
            </Button>
          </Box>
        </>
      )}

      {/* Password Modal */}
      <Dialog 
        open={openPasswordModal} 
        onClose={handlePasswordModalClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Download Data
          <IconButton
            aria-label="close"
            onClick={handlePasswordModalClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, mb: 2 }}>
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              variant="outlined"
              sx={{ mb: 3 }}
            />
            <FormControl fullWidth>
              <InputLabel>Format</InputLabel>
              <Select
                value={downloadFormat}
                onChange={(e) => setDownloadFormat(e.target.value)}
                label="Format"
              >
                <MenuItem value="excel">Excel</MenuItem>
                <MenuItem value="pdf">PDF</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handlePasswordModalClose} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleDownloadConfirm} 
            variant="contained" 
            color="primary"
            disabled={!password}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default HearingAidTraining;